import { useMemo, useState } from "react";

import { groupBy } from "lodash";

import { ModalPanel, PrimaryButton, TertiaryButton } from "@components/v2";
import { TextInput } from "@components/v2/inputs";
import { Select } from "@components/v2/inputs/select";
import { type SalesMetricFilterInput } from "@north-beam/nb-client-side";
import { Modal } from "@shared/modal";

import { useTableStateDimensions, useSalesV4Context } from "../hooks";
import { type TableState } from "../types";

const OPERATOR_OPTIONS = [
  { label: "Greater than", value: "gt" },
  { label: "Greater than or Equal to", value: "gte" },
  { label: "Less than", value: "lt" },
  { label: "Less than or Equal to", value: "lte" },
  { label: "Equal to", value: "eq" },
];

interface Props {
  isOpen: boolean;
  metricFilters: SalesMetricFilterInput[];
  tableState?: TableState;
  onClose: () => void;
  onChange: (metricFilters: SalesMetricFilterInput[]) => void;
}

export const MetricFilterModal = ({
  isOpen,
  metricFilters,
  tableState,
  onClose,
  onChange,
}: Props) => {
  return (
    <Modal
      variant="v2"
      contentClassName="max-w-[720px] h-auto"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Content
        metricFilters={metricFilters}
        tableState={tableState}
        onChange={onChange}
        onClose={onClose}
      />
    </Modal>
  );
};

const Content = ({
  metricFilters,
  tableState,
  onChange,
  onClose,
}: Omit<Props, "isOpen">) => {
  const { metrics } = useSalesV4Context();
  const [localFilters, setLocalFilters] = useState(
    metricFilters.length > 0
      ? metricFilters
      : [{ metricId: "", operator: "gt", value: 0 }],
  );

  const metricOptions = useMemo(
    () =>
      metrics.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [metrics],
  );

  const handleFilterChange = (index: number, key: string, value: any) =>
    setLocalFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      updatedFilters[index] = { ...updatedFilters[index], [key]: value };
      return updatedFilters;
    });

  const handleRemoveFilter = (index: number) =>
    setLocalFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));

  const handleAddFilter = () =>
    setLocalFilters([
      ...localFilters,
      { metricId: "", operator: "gt", value: 0 },
    ]);

  const handleApplyFilters = () => {
    onChange(
      localFilters.filter(
        (filter) =>
          filter.metricId !== "" &&
          filter.operator !== "" &&
          typeof filter.value === "number",
      ),
    );
    onClose();
  };

  const canApplyFilters = localFilters.some(
    (filter) =>
      filter.metricId !== "" &&
      filter.operator !== "" &&
      typeof filter.value === "number",
  );

  const renderBreakdownFilters = () => {
    if (!tableState?.breakdownFilters?.length) {
      return null;
    }

    return (
      <div className="px-4 pb-4 rounded border border-v2-tertiary">
        {Object.entries(groupBy(tableState.breakdownFilters, "breakdown")).map(
          ([breakdown, filters]) => (
            <div className="flex flex-col gap-2 text-xs pt-4" key={breakdown}>
              {breakdown}:
              <div className="flex flex-wrap gap-2" key={breakdown}>
                {filters.map((f) => (
                  <div
                    key={f.value}
                    className="bg-v2-highlight-notice px-2 py-1 rounded-xl border border-v2-notice text-v2-action-positive"
                  >
                    {f.value}
                  </div>
                ))}
              </div>
            </div>
          ),
        )}
      </div>
    );
  };

  return (
    <ModalPanel className="h-full">
      <div className="font-semibold mb-6">Data Filters</div>
      <div className="flex flex-col gap-4 mb-6">
        {localFilters.map((filter, index) => (
          <div key={index} className="flex gap-3 items-center">
            <Select
              className="w-60 [&_button]:!w-full"
              placeholder="Select Column"
              options={metricOptions}
              value={filter.metricId}
              onChange={(value) => handleFilterChange(index, "metricId", value)}
              hasSearch
            />
            <Select
              className="w-48 [&_button]:!w-full"
              placeholder="Greater Than"
              options={OPERATOR_OPTIONS}
              value={filter.operator}
              onChange={(value) => handleFilterChange(index, "operator", value)}
            />
            <TextInput
              className="w-32"
              placeholder="Filter value"
              type="number"
              value={String(filter.value ?? "")}
              onChange={(e) =>
                handleFilterChange(index, "value", e.target.valueAsNumber)
              }
            />
            <TertiaryButton size="sm" onClick={() => handleRemoveFilter(index)}>
              <i className="fa-regular fa-trash uppercase text-mars"></i>
            </TertiaryButton>
          </div>
        ))}
        <TertiaryButton
          size="sm"
          className="flex items-center text-primary font-semibold w-fit"
          onClick={handleAddFilter}
        >
          <i className="fa-regular fa-plus text-primary" />
          FILTER
        </TertiaryButton>
      </div>
      {renderBreakdownFilters()}
      <div className="flex justify-between pt-4">
        <div>
          <TertiaryButton
            disabled={metricFilters.length === 0 && localFilters.length === 0}
            onClick={() => {
              onChange([]);
              onClose();
            }}
          >
            RESET
          </TertiaryButton>
        </div>
        <div className="flex items-center gap-2">
          <TertiaryButton onClick={() => onClose()}>CANCEL</TertiaryButton>
          <PrimaryButton
            disabled={!canApplyFilters}
            onClick={handleApplyFilters}
          >
            APPLY FILTERS
          </PrimaryButton>
        </div>
      </div>
    </ModalPanel>
  );
};
